import * as React from 'react'
import { Plus, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CategoriaGastoBadge } from './CategoriaGastoBadge'
import { COLORES_IMPUESTO, type ColorImpuesto } from '@/types/impuestos'
import {
  GRUPOS_RESULTADO,
  GRUPO_RESULTADO_LABEL,
  type GrupoResultado,
} from '@/types/estadoResultado'

export interface CrearCategoriaGastoData {
  nombre: string
  tipo: 'gasto' | 'retiro'
  color: string
  grupo_resultado: GrupoResultado | null
}

interface CrearCategoriaGastoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCrear: (data: CrearCategoriaGastoData) => Promise<void>
  nombreInicial?: string
  isCreating?: boolean
}

const COLORES_DISPONIBLES: ColorImpuesto[] = [
  'gray', 'slate', 'red', 'rose', 'orange', 'amber', 'yellow',
  'green', 'emerald', 'teal', 'cyan', 'blue', 'sky', 'indigo', 'violet', 'purple',
]

// 'ingreso' existe en el CHECK pero no se ofrece acá (slice de gastos del estudio).
// ORR-181: el tipo distingue gasto vs retiro (naturaleza). "Operativo/Comercial/..."
// es el grupo del estado de resultado (otro campo). El label evita la palabra
// "operativo" para no confundirse con el grupo homónimo.
const TIPOS: { value: 'gasto' | 'retiro'; label: string }[] = [
  { value: 'gasto', label: 'Gasto del estudio' },
  { value: 'retiro', label: 'Retiro de socios' },
]

export function CrearCategoriaGastoDialog({
  open,
  onOpenChange,
  onCrear,
  nombreInicial = '',
  isCreating = false,
}: CrearCategoriaGastoDialogProps) {
  const [nombre, setNombre] = React.useState(nombreInicial)
  const [tipo, setTipo] = React.useState<'gasto' | 'retiro'>('gasto')
  const [color, setColor] = React.useState<ColorImpuesto>('gray')
  const [grupo, setGrupo] = React.useState<GrupoResultado | null>(null)

  React.useEffect(() => {
    if (open) {
      setNombre(nombreInicial)
      setTipo('gasto')
      setColor('gray')
      setGrupo(null)
    }
  }, [open, nombreInicial])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!nombre.trim()) return
    try {
      await onCrear({
        nombre: nombre.trim(),
        tipo,
        color,
        grupo_resultado: tipo === 'gasto' ? grupo : null,
      })
      onOpenChange(false)
    } catch (error) {
      console.error('Error creando categoría de gasto:', error)
      // No cerramos el dialog para permitir reintento
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Nueva categoría de gasto
            </DialogTitle>
            <DialogDescription>
              Agregá una categoría para clasificar los gastos del estudio
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="nombre">Nombre *</Label>
              <Input
                id="nombre"
                placeholder="Ej: Software y suscripciones"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                autoFocus
              />
            </div>

            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select value={tipo} onValueChange={(v) => setTipo(v as 'gasto' | 'retiro')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Los retiros de socios se informan aparte: no afectan el resultado.
              </p>
            </div>

            {tipo === 'gasto' && (
              <div className="space-y-2">
                <Label>Grupo del estado de resultado</Label>
                <Select
                  value={grupo ?? 'sin'}
                  onValueChange={(v) => setGrupo(v === 'sin' ? null : (v as GrupoResultado))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sin">Sin clasificar</SelectItem>
                    {GRUPOS_RESULTADO.map((g) => (
                      <SelectItem key={g} value={g}>
                        {GRUPO_RESULTADO_LABEL[g]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Define en qué renglón del estado de resultado cae este gasto.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-1.5">
                {COLORES_DISPONIBLES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setColor(c)}
                    className={`w-6 h-6 rounded-md border-2 transition-all ${COLORES_IMPUESTO[c].bg} ${
                      color === c ? 'border-foreground scale-110' : 'border-transparent hover:scale-105'
                    }`}
                    title={c}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-2 pt-2 border-t">
              <Label className="text-muted-foreground">Vista previa</Label>
              <CategoriaGastoBadge nombre={nombre || 'Categoría'} color={color} size="md" />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isCreating}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={!nombre.trim() || isCreating}>
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creando...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear categoría
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
